import axios from 'axios';
import { log, logError } from './logger';

const AUTH_TOKEN_KEY = process.env.REACT_APP_AUTH_TOKEN_KEY;

class Api {
  constructor(navigate) {
    this.navigate = navigate;
    this.instance = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      xsrfCookieName: 'csrftoken',
      xsrfHeaderName: 'X-CSRFToken'
    });

    // Set the Authorization header if token exists
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      this.instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      log('Token found in localStorage', { tokenKey: AUTH_TOKEN_KEY });
    } else {
      log('No token found in localStorage', { tokenKey: AUTH_TOKEN_KEY });
    }

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        // Always set the latest token
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        
        // Debug log to show headers
        console.log('Request Headers:', {
          url: config.url,
          headers: config.headers,
          token: token ? 'present' : 'missing',
          tokenKey: AUTH_TOKEN_KEY
        });

        const stack = new Error().stack;
        const stackLines = stack.split('\n');
        const callerInfo = stackLines
          .slice(2, 5)
          .map(line => line.trim())
          .filter(line => !line.includes('node_modules'));

        log('🔍 API Request:', {
          endpoint: `${config.method.toUpperCase()} ${config.url}`,
          data: config.data,
          timestamp: new Date().toISOString(),
          caller: callerInfo,
          reactComponent: config.headers['X-React-Component'] || 'unknown',
          hasToken: !!token
        });

        // Add CSRF token if available
        const csrfToken = document.cookie.split('; ')
          .find(row => row.startsWith('csrftoken='))
          ?.split('=')[1];
        
        if (csrfToken) {
          config.headers['X-CSRFToken'] = csrfToken;
        }

        // Ensure withCredentials is set for all requests
        config.withCredentials = true;

        return config;
      },
      (error) => {
        logError(error, 'API Request Error', {
          url: error.config?.url,
          method: error.config?.method
        });
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response) => {
        // Log response details
        log('API Response:', {
          status: response.status,
          data: response.data,
          headers: response.headers
        });
        return response;
      },
      async (error) => {
        const isTokenError = 
          error.response?.status === 401 || 
          error.response?.data?.error === 'invalid_token';

        if (isTokenError) {
          log('Token expiration detected', {
            url: error.config?.url,
            status: error.response?.status
          });

          // Clear auth data
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem('userInfo');

          log('Redirecting to login page due to authentication failure');
          this.navigate('/login');
          return Promise.reject(new Error('Session expired. Please log in again.'));
        }
        
        // Log detailed error information
        logError(error, 'API Response Error', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          method: error.config?.method,
          data: error.response?.data,
          headers: error.response?.headers,
          message: error.message
        });
        return Promise.reject(error);
      }
    );
  }

  async get(endpoint, options = {}) {
    return await this.instance.get(endpoint, options);
  }

  async post(endpoint, data) {
    return await this.instance.post(endpoint, data);
  }

  async patch(endpoint, data) {
    return await this.instance.patch(endpoint, data);
  }

  async delete(endpoint) {
    return await this.instance.delete(endpoint);
  }

  async put(endpoint, data) {
    return await this.instance.put(endpoint, data);
  }
}

let apiInstance = null;

export const createApi = (navigate) => {
  apiInstance = new Api(navigate);
  return apiInstance;
};

export const getApi = () => {
  if (!apiInstance) {
    throw new Error('API not initialized. Call createApi first.');
  }
  return apiInstance;
};

export default Api;
